/**
 * Client-side auth/permission cache. The real auth token is an httpOnly cookie;
 * this just caches the user's profile + effective (module:action) set for UI
 * gating. Backed by an in-memory value + localStorage, with a subscribe() so
 * React can re-render on change (used by useCan/useAuth).
 *
 * The store stays app-owned (key `app:auth`, which the e2e suite seeds); the permission
 * logic is @dreamtree-org/tenancy/react's.
 */
import { hasPermission as checkPermission, colonFormat } from '@dreamtree-org/tenancy/react';

const KEY = 'app:auth';
const listeners = new Set();

let current = load();

function load() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || 'null');
  } catch {
    return null;
  }
}

export function getAuth() {
  return current;
}

export function setAuth(user) {
  current = user;
  try {
    localStorage.setItem(KEY, JSON.stringify(user));
  } catch {}
  emit();
}

export function clearAuth() {
  current = null;
  try {
    localStorage.removeItem(KEY);
  } catch {}
  emit();
}

export function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function emit() {
  listeners.forEach((l) => l());
}

/**
 * The server's wire format (services/rbac/effectivePermissions.js): "module:action" for
 * org-scoped grants, "module:action:all" for all-scoped ones. rbac-authz/react's
 * colonFormat reads it as canonical `module.action.org` / `module.action.all`, so every
 * check below runs the engine's own scope ranking (an `all` grant satisfies `org`).
 */
export const PERMISSION_FORMAT = colonFormat('org');

/**
 * Capability check: superadmin bypasses; else does the user hold module.action.<scope>?
 * Delegates to @dreamtree-org/tenancy/react's hasPermission — `scope: 'all'` asks for the
 * platform-wide grant (gating e.g. the global connection categories/providers, which the
 * server checks on `all`); an org grant does NOT satisfy it, an all grant satisfies both.
 */
export function hasPermission(user, module, action, scope = 'org') {
  if (!user) return false;
  return checkPermission(user.permissions, `${module}.${action}.${scope}`, {
    isSuperAdmin: !!user.isAdmin,
    format: PERMISSION_FORMAT,
  });
}
